import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { StandardTable } from '../../../../shared/components/standard-table/standard-table/standard-table';
import { FormsModule } from '@angular/forms';
import { ResultService } from '../../../../core/services/result.service';
import { UploadImageService } from '../../../../core/services/uploadimage.service';
import { AlertService } from '../../../../shared/components/alert/alert';
import { firstValueFrom } from 'rxjs';
import { TranslationService } from '../../../../core/services/translation.service';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-result',
  imports: [CommonModule, StandardTable, FormsModule, TranslatePipe],
  templateUrl: './result.html',
  styleUrl: './result.css',
})
export class Result {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  columnList = [
    { name: 'column.icon', key: 'icon', sortable: false },
    { name: 'column.date', key: 'date', sortable: false },
    { name: 'column.action', key: 'actions', sortable: false },
  ];

  tableDataFilters: any[] = [];
  totalItems = 0;
  isLoading = false;

  currentPage = 1;
  pageSize = 10;
  searchQuery = '';

  results: any = [];

  isSaving = false;
  isModalOpen = false;
  isEditMode = false;
  editingId: string | null = null;

  newResult = {
    image: '',
  };

  editImage: string | null = null;
  imagePreview: string | null = null;
  imageFile: File | null = null;
  screenshotBase64: string | null = null;

  constructor(
    private resultService: ResultService,
    private uploadImageService: UploadImageService,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.loadLotteries();
  }

  triggerFileInput() {
    this.fileInput.nativeElement.value = '';
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagePreview = e.target.result;
        this.imageFile = file;
        this.screenshotBase64 = e.target.result as string;
        this.cdr.markForCheck();
      };
      reader.readAsDataURL(file);
    }
  }

  loadLotteries() {
    this.isLoading = true;

    const payload = {
      page: this.currentPage.toString(),
      limit: this.pageSize.toString(),
      search: this.searchQuery,
    };

    this.resultService.getAllResults(payload).subscribe({
      next: (res: any) => {
        if (res.returncode === '300') {
          // this.tableDataFilters = res.data.data;
          this.tableDataFilters = res.data.data.map((t: any) => ({
            ...t,
            icon: t.image,
          }));
          this.totalItems = res.data.meta.totalItems || res.data.meta.total || 0;
        } else {
          this.tableDataFilters = [];
          this.totalItems = 0;
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch results in admin table:', err);
        this.tableDataFilters = [];
        this.totalItems = 0;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  search(value: string) {
    this.searchQuery = value;
    this.currentPage = 1;
    this.loadLotteries();
  }

  onSort(column: any) {
    console.log('Sort:', column);
  }

  onPageChange(page: any) {
    this.currentPage = page;
    this.loadLotteries();
  }

  onClickRow(row: any) {
    console.log('Row clicked:', row);
  }

  openCreateModal() {
    this.isEditMode = false;
    this.editingId = null;
    this.isModalOpen = true;
    this.imagePreview = null;
    this.imageFile = null;
    this.screenshotBase64 = null;
    this.newResult = {
      image: '',
    };
  }

  onEdit(row: any) {
    this.isEditMode = true;
    this.editingId = row.id;
    this.isModalOpen = true;
    this.imagePreview = row.icon;
    this.editImage = row.icon;
    this.imageFile = row.icon;
    this.screenshotBase64 = row.icon;
    this.newResult = {
      image: row.image,
    };
  }

  onDelete(row: any) {
    if (confirm(this.translationService.translate('alert.deleteConfirm'))) {
      this.resultService.deleteResult(row.id).subscribe({
        next: (res: any) => {
          if (res.returncode === '300') {
             this.alertService.success(this.translationService.translate('alert.deleteSuccess'));
            this.loadLotteries();
          } else {
             this.alertService.error(res.message || this.translationService.translate('alert.deleteError'));
          }
        },
        error: (err) => {
          console.error('Error deleting result:', err);
           this.alertService.error(this.translationService.translate('alert.errorOccurred'));
        },
      });
    }
  }

  closeModal() {
    this.isModalOpen = false;
  }

  async submit() {
    if (!this.imageFile) return;

    this.isSaving = true;

    try {
      // Upload image only if changed
      if (this.imagePreview !== this.editImage) {
        const res = await firstValueFrom(
          this.uploadImageService.uploadImageForAdmin('result', this.imageFile),
        );

        if (res.returncode !== '300') {
          throw new Error(res.message);
        }

        this.newResult.image = res.data.url;
      }

      const request$ = this.isEditMode
        ? this.resultService.updateResult({ ...this.newResult, id: this.editingId })
        : this.resultService.createResult(this.newResult);

      request$.subscribe({
        next: (res: any) => this.handleSuccess(res),
        error: (err) => this.handleError(err),
      });
    } catch (error: any) {
      this.isSaving = false;
      this.alertService.error(
        error.message || `Failed to ${this.isEditMode ? 'update' : 'create'} result`,
      );
      this.cdr.detectChanges();
    }
  }

  private handleSuccess(res: any) {
    this.isSaving = false;

    if (res.returncode === '300') {
       this.alertService.success(this.translationService.translate(this.isEditMode ? 'alert.updateSuccess' : 'alert.createSuccess'));
      this.closeModal();
      this.loadLotteries();
    } else {
       this.alertService.error(
        res.message || this.translationService.translate(this.isEditMode ? 'alert.updateError' : 'alert.createError'),
      );
    }

    this.cdr.detectChanges();
  }

  private handleError(err: any) {
    this.isSaving = false;
    console.error(`Error ${this.isEditMode ? 'updating' : 'creating'} result:`, err);

     this.alertService.error(this.translationService.translate('alert.errorOccurred'));

    this.cdr.detectChanges();
  }
}
